import type { Entity } from '@backstage/catalog-model';
import { useEntityList } from '@backstage/plugin-catalog-react';
import {
  TemplateGroups,
  type TemplateGroupsProps,
} from '@backstage/plugin-scaffolder-react/alpha';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import type { WorkflowGroup } from './workflowGroups';

const useStyles = makeStyles(theme => ({
  section: {
    backgroundColor: 'transparent',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:before': { display: 'none' },
    '&.Mui-expanded': { margin: 0 },
  },
  summary: {
    minHeight: theme.spacing(10),
    '&.Mui-expanded': { minHeight: theme.spacing(10) },
  },
  summaryContent: {
    margin: theme.spacing(1.5, 0),
    '&.Mui-expanded': { margin: theme.spacing(1.5, 0) },
  },
  title: {
    fontSize: 26,
  },
  templates: {
    '& > article': { padding: 0 },
  },
}));

type Props = Omit<TemplateGroupsProps, 'groups' | 'templateFilter'> & {
  groups: WorkflowGroup[];
  templateFilter?: (entity: Entity) => boolean;
};

export function CollapsibleWorkflowGroups(props: Props) {
  const classes = useStyles();
  const { entities, loading, error } = useEntityList();
  const visibleGroups = props.groups.filter(group =>
    entities.some(
      entity =>
        entity.kind.toLowerCase() === 'template' &&
        (!props.templateFilter || props.templateFilter(entity)) &&
        group.filter(entity),
    ),
  );

  // Retain the scaffolder's loading, error and no-match states.
  if (loading || error || !visibleGroups.length)
    return <TemplateGroups {...props} />;

  return (
    <>
      {visibleGroups.map(group => {
        const id = `workflow-group-${encodeURIComponent(group.id)}`;
        return (
          <Accordion
            key={group.id}
            defaultExpanded
            elevation={0}
            square
            className={classes.section}
          >
            <AccordionSummary
              classes={{
                root: classes.summary,
                content: classes.summaryContent,
              }}
              expandIcon={<ExpandMoreIcon />}
              id={`${id}-heading`}
              aria-controls={`${id}-content`}
            >
              <Typography component="h2" variant="h5" className={classes.title}>
                {group.title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box width="100%" className={classes.templates}>
                <TemplateGroups
                  {...props}
                  groups={[{ ...group, title: null }]}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </>
  );
}
